export type DemoSymbol = 'ETH' | 'WBTC' | 'USDT' | 'USDC' | 'DAI';

export interface AnchorPrice {
  symbol: DemoSymbol;
  anchorUsd: number;
  intradayVolPct: number;
  maxDriftPct: number;
}

// Fallbacks used when the live oracle fetch fails. Stablecoins are pegged,
// ETH/WBTC values sit in a current plausible range; the runtime fetches
// fresh prices from CoinGecko at boot so these are only a safety net.
export const FALLBACK_ANCHORS: Record<DemoSymbol, AnchorPrice> = {
  ETH:  { symbol: 'ETH',  anchorUsd: 3500,  intradayVolPct: 0.0015, maxDriftPct: 0.03 },
  WBTC: { symbol: 'WBTC', anchorUsd: 95000, intradayVolPct: 0.0015, maxDriftPct: 0.03 },
  USDT: { symbol: 'USDT', anchorUsd: 1,     intradayVolPct: 0.0001, maxDriftPct: 0.002 },
  USDC: { symbol: 'USDC', anchorUsd: 1,     intradayVolPct: 0.0001, maxDriftPct: 0.002 },
  DAI:  { symbol: 'DAI',  anchorUsd: 1,     intradayVolPct: 0.0001, maxDriftPct: 0.002 },
};

const COINGECKO_IDS: Record<DemoSymbol, string> = {
  ETH: 'ethereum',
  WBTC: 'wrapped-bitcoin',
  USDT: 'tether',
  USDC: 'usd-coin',
  DAI: 'dai',
};

export async function fetchLivePrices(): Promise<Partial<Record<DemoSymbol, number>>> {
  const ids = Object.values(COINGECKO_IDS).join(',');
  const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 4000);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) return {};
    const json = await res.json();
    const out: Partial<Record<DemoSymbol, number>> = {};
    (Object.keys(COINGECKO_IDS) as DemoSymbol[]).forEach(sym => {
      const p = json?.[COINGECKO_IDS[sym]]?.usd;
      if (typeof p === 'number' && p > 0) out[sym] = p;
    });
    return out;
  } catch (e) {
    console.warn('[priceAnchors] coingecko fetch failed, using fallback anchors', e);
    return {};
  } finally {
    clearTimeout(timer);
  }
}

export async function resolveAnchors(): Promise<Record<DemoSymbol, AnchorPrice>> {
  const live = await fetchLivePrices();
  const out = { ...FALLBACK_ANCHORS };
  (Object.keys(out) as DemoSymbol[]).forEach(sym => {
    const p = live[sym];
    if (p !== undefined) out[sym] = { ...out[sym], anchorUsd: p };
  });
  return out;
}
